/**
 * QUIPWA — forced light mode
 *
 * A button, not a theme. It shows a light version of whatever theme is
 * active and keeps that theme's identity: its accent hue (also as a faint
 * tint of the page), its dark ink, its fonts and shapes. Like night mode, it
 * is a palette derived from the profile at apply time; the theme itself never
 * changes, so turning it off brings the theme back exactly.
 *
 * ```ts
 * const shown = state.lightMode ? lightVariant(profile) : profile
 * ```
 *
 * Every text colour is checked against the new background (AAA for titles,
 * 7:1 for body text so the muted step still has room), and the accent darkens
 * just enough to carry text in the background colour, the way buttons use it.
 */
import { fillForInk, isLightColor, mix, readableTextColor } from './color-utils'
import { findPreset } from './presets'
import type { ThemeProfile } from './theme-store'

/** The warm off-white the light page leans toward (Papel's family). */
const PAPER = '#F7F4EE'
/** How much of the theme's accent tints the page: enough to tell themes apart, not to colour it. */
const TINT = 0.05
const TITLE_CONTRAST = 7
const BODY_CONTRAST = 7

/** A soft shadow that reads on paper (a dark theme's shadow is a smudge there). */
const LIGHT_SHADOW = findPreset('paper')?.effects.cardShadow ?? '0 6px 24px rgba(30,42,34,0.12)'

/** The light version of a theme. Light themes are returned unchanged. */
export function lightVariant(profile: ThemeProfile): ThemeProfile {
  const c = profile.colors
  if (isLightColor(c.bgBase)) return profile

  const bgBase = mix(PAPER, c.primary, TINT)
  const surface = mix('#FFFFFF', bgBase, 0.35)
  // The ink is the theme's own dark background, so a green theme gets
  // green-black text and a blue one blue-black, pushed until it is crisp.
  const textTitle = readableTextColor(mix(c.bgBase, '#000000', 0.2), bgBase, TITLE_CONTRAST)
  const textBody = readableTextColor(mix(textTitle, bgBase, 0.3), bgBase, BODY_CONTRAST)

  const bg = profile.background
  const background: ThemeProfile['background'] =
    bg.type === 'image'
      ? { ...bg, opacity: Math.min(bg.opacity, 0.3) }
      : bg.type === 'gradient'
        ? { ...bg, type: 'solid', value: bgBase }
        : { ...bg, value: bgBase }

  return {
    ...profile,
    colors: {
      // Buttons put background-coloured text on the accent: keep its hue, darken until AA.
      primary: fillForInk(c.primary, bgBase),
      bgBase,
      surface,
      textTitle,
      textBody,
    },
    background,
    effects: {
      ...profile.effects,
      cardShadow: profile.effects.cardShadow === 'none' ? 'none' : LIGHT_SHADOW,
    },
  }
}
